import { useState, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from './useRedux'
import { userSelector } from '@/pages/user/selectors'
import { updateUser, setIsLoading } from '@/pages/user/userSlice'
import {
  getWebSessionToken,
  isWebSessionValid,
  getProfileWithToken,
  clearWebSessionToken,
} from '@/services/webSession'

interface UseWebSessionReturn {
  /** The web session access token, or null if not in web session mode or expired */
  token: string | null
  /** Whether we are in web session mode (token exists in sessionStorage) */
  isWebSession: boolean
  /** Whether the web session has expired */
  isExpired: boolean
  /** Whether the hook is still initializing (loading user profile) */
  isInitializing: boolean
  /** Error message if session initialization failed */
  sessionError: string | null
}

/**
 * Hook for managing web session authentication.
 * Web sessions are fully isolated from the main cookie-based auth:
 * - Uses sessionStorage instead of localStorage/cookies
 * - Token survives page refresh but not tab/browser close
 * - Does not affect existing logged-in users on the main website
 */
export function useWebSession(): UseWebSessionReturn {
  const dispatch = useAppDispatch()
  const { user } = useAppSelector(userSelector)

  const [isInitializing, setIsInitializing] = useState(true)
  const [sessionError, setSessionError] = useState<string | null>(null)

  const token = getWebSessionToken()
  const isValid = isWebSessionValid()
  const isWebSession = !!token
  const isExpired = isWebSession && !isValid

  useEffect(() => {
    const initializeSession = async () => {
      // If no web session token, nothing to do
      if (!token) {
        setIsInitializing(false)
        return
      }

      // If token expired, clear and show error
      if (!isValid) {
        clearWebSessionToken()
        setSessionError('Session expired')
        setIsInitializing(false)
        return
      }

      // If user already loaded, we're done
      if (user) {
        setIsInitializing(false)
        return
      }

      // Fetch user profile using web session token
      try {
        dispatch(setIsLoading(true))
        const userProfile = await getProfileWithToken(token)
        dispatch(updateUser(userProfile))
        setIsInitializing(false)
      } catch (error) {
        console.error('Failed to fetch user profile for web session:', error)
        clearWebSessionToken()
        setSessionError('Failed to initialize session')
        setIsInitializing(false)
      } finally {
        dispatch(setIsLoading(false))
      }
    }

    initializeSession()
  }, [token, isValid, user, dispatch])

  return {
    token: isValid ? token : null,
    isWebSession,
    isExpired,
    isInitializing,
    sessionError,
  }
}
